import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import {
  Heart,
  Target,
  Users,
  Shield,
  ArrowRight,
  Building2,
  GraduationCap,
  Globe,
} from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: 'By students, for students',
    description: 'StudyStay is geboren uit de frustratie van studenten die zelf op zoek gingen naar een kot. We begrijpen de noden omdat we ze zelf hebben ervaren.',
  },
  {
    icon: Shield,
    title: 'Veilig & transparant',
    description: 'Geverifieerde profielen, digitale contracten met audit trail en GDPR-compliant databeheer. Vertrouwen staat centraal.',
  },
  {
    icon: Target,
    title: 'Eenvoud boven alles',
    description: 'Geen ingewikkelde processen. Zoeken, chatten, bezichtigen en tekenen — alles in een paar klikken.',
  },
  {
    icon: Globe,
    title: 'Toegankelijk voor iedereen',
    description: 'Of je nu één kamer verhuurt of honderden — StudyStay is gebouwd om mee te schalen met jouw noden.',
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <PublicNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #5EABEB 0%, transparent 70%)' }} />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #5EABEB 0%, transparent 70%)' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-sm font-medium text-[#2B6FA6] mb-8">
            <Heart className="w-4 h-4" />
            Over StudyStay
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
            Het platform dat studenten en verhuurders{' '}
            <span className="bg-gradient-to-r from-[#5EABEB] to-[#2B6FA6] bg-clip-text text-transparent">
              samenbrengt
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            StudyStay maakt het zoeken, verhuren en beheren van studentenkamers eenvoudig, veilig en digitaal. Van het eerste contact tot het getekende contract.
          </p>
        </div>
      </section>

      {/* Missie */}
      <section className="py-16 sm:py-20" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Onze missie</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Elk academiejaar staan duizenden studenten voor dezelfde uitdaging: een betaalbare, veilige studentenkamer vinden. Tegelijkertijd worstelen verhuurders met administratie, communicatie en contractbeheer.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                StudyStay brengt beide werelden samen op één platform. We digitaliseren het volledige verhuurproces — van listing tot getekend contract — zodat studenten sneller hun droomkot vinden en verhuurders efficiënter kunnen werken.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm text-center">
                <GraduationCap className="w-8 h-8 text-[#5EABEB] mx-auto mb-3" />
                <p className="text-2xl font-bold text-slate-900">100%</p>
                <p className="text-sm text-slate-500">Gratis voor studenten</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm text-center">
                <Building2 className="w-8 h-8 text-[#5EABEB] mx-auto mb-3" />
                <p className="text-2xl font-bold text-slate-900">België</p>
                <p className="text-sm text-slate-500">Beschikbaar in heel België</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm text-center">
                <Shield className="w-8 h-8 text-[#5EABEB] mx-auto mb-3" />
                <p className="text-2xl font-bold text-slate-900">GDPR</p>
                <p className="text-sm text-slate-500">Volledig compliant</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm text-center">
                <Users className="w-8 h-8 text-[#5EABEB] mx-auto mb-3" />
                <p className="text-2xl font-bold text-slate-900">2-zijdig</p>
                <p className="text-sm text-slate-500">Studenten & verhuurders</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Waarden */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Onze waarden</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Wat ons drijft bij alles wat we bouwen.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-6 border border-slate-200/80 hover:border-[#5EABEB]/30 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-[#5EABEB]" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bedrijf */}
      <section className="py-16 sm:py-20" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Het bedrijf</h2>
          <p className="text-lg text-slate-600 mb-4 leading-relaxed">
            StudyStay is een product van <strong className="text-slate-900">Nolf Technologies OÜ</strong>, gevestigd in Tallinn, Estland. We bouwen technologie die het dagelijks leven van studenten en verhuurders eenvoudiger maakt.
          </p>
          <p className="text-sm text-slate-500">
            Tornimäe tn 5, 10145 Tallinn, Estonia
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Klaar om te starten?</h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            Of je nu een student bent op zoek naar een kot, of een verhuurder die efficiënter wil werken — StudyStay is er voor jou.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/user-type-login?source=about&userType=tenant"
              className="group inline-flex items-center justify-center px-8 py-4 text-white font-semibold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 w-full sm:w-auto"
              style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
            >
              Ik zoek een kot
              <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/voor-verhuurders"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-800 font-semibold text-lg rounded-2xl shadow-sm border-2 border-slate-200 hover:border-[#5EABEB] hover:shadow-md transition-all duration-300 w-full sm:w-auto"
            >
              Ik ben verhuurder
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200/80 bg-gradient-to-b from-white to-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Nolf Technologies OÜ. Alle rechten voorbehouden.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-[#2B6FA6] transition-colors">Privacybeleid</Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-[#2B6FA6] transition-colors">Voorwaarden</Link>
              <Link to="/contact" className="text-sm text-slate-500 hover:text-[#2B6FA6] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
